/**
 * UTM attribution — capture marketing parameters on first landing.
 *
 * Flow:
 *   1. On every page load, read UTM + referrer + landing URL from window.
 *   2. If we don't already have a "first touch" in localStorage, store it.
 *      Subsequent visits never overwrite the first touch (that's the point).
 *   3. Always refresh "last touch" so analytics tools can use either.
 *   4. `getAttributionForSignup()` returns the first-touch payload — attach
 *      this to `/api/auth/register` so the user's attribution is pinned at
 *      signup time regardless of which tab they finally converted in.
 *
 * Privacy: we don't capture query params other than UTM fields and `gclid`.
 * Referrer is trimmed to origin (no paths) to avoid accidental PII in
 * search/referrer URLs.
 */

const FIRST_TOUCH_KEY = "ls_attr_first";
const LAST_TOUCH_KEY = "ls_attr_last";

const UTM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

function readUtmFromUrl() {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    UTM_FIELDS.forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v.slice(0, 128); // defensive cap
    });
    const gclid = params.get("gclid");
    if (gclid) out.gclid = gclid.slice(0, 128);
    return out;
  } catch {
    return {};
  }
}

function referrerOrigin() {
  if (typeof document === "undefined") return null;
  const r = document.referrer || "";
  if (!r) return null;
  try {
    const u = new URL(r);
    // Drop path/query to minimize any accidental PII in the referrer.
    return u.origin;
  } catch {
    return null;
  }
}

function landingPath() {
  if (typeof window === "undefined") return null;
  try {
    return (window.location.pathname || "/") + (window.location.search ? "?..." : "");
  } catch {
    return null;
  }
}

function readStored(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStored(key, obj) {
  try {
    window.localStorage.setItem(key, JSON.stringify(obj));
  } catch {
    // ignore quota / disabled storage
  }
}

/**
 * Call once very early (index.js / App mount). Idempotent; safe to call on
 * every page load.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  const utm = readUtmFromUrl();
  const touch = {
    ...utm,
    referrer: referrerOrigin(),
    landing: landingPath(),
    captured_at: new Date().toISOString(),
  };

  // Only persist a first-touch if there's any marketing signal at all.
  // Direct-to-homepage visits without UTM + without referrer don't create
  // a first-touch record (avoids clobbering real attribution later).
  const hasSignal = Object.keys(utm).length > 0 || !!touch.referrer;

  if (hasSignal) {
    if (!readStored(FIRST_TOUCH_KEY)) {
      writeStored(FIRST_TOUCH_KEY, touch);
    }
    writeStored(LAST_TOUCH_KEY, touch);
  }
}

/** First-touch payload for the /api/auth/register body. May be null. */
export function getAttributionForSignup() {
  return readStored(FIRST_TOUCH_KEY);
}

/** Lightweight dict of {utm_source, utm_medium, utm_campaign} for event params. */
export function getAttributionEventParams() {
  const a = readStored(FIRST_TOUCH_KEY) || {};
  const out = {};
  if (a.utm_source) out.utm_source = a.utm_source;
  if (a.utm_medium) out.utm_medium = a.utm_medium;
  if (a.utm_campaign) out.utm_campaign = a.utm_campaign;
  if (a.utm_content) out.utm_content = a.utm_content;
  if (a.utm_term) out.utm_term = a.utm_term;
  if (a.referrer) out.referrer = a.referrer;
  return out;
}

/** Test helper — not exported from index; safe to leave for emergency reset. */
export function clearAttribution() {
  try {
    window.localStorage.removeItem(FIRST_TOUCH_KEY);
    window.localStorage.removeItem(LAST_TOUCH_KEY);
  } catch {
    // ignore
  }
}
